import PropTypes from 'prop-types';

import {
  useColorModeValue,
  Box,
  Checkbox,
  Flex,
  Tag,
  Text,
  Tooltip,
  TagLeftIcon,
  TagLabel,
  Button,
} from '@chakra-ui/react';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import { FaCheckCircle, FaPen } from 'react-icons/fa';

import SubQuestIcon from 'components/subQuest/SubQuestIcon';
import IdTag from 'components/common/fields/IdTag';
import TagGroup from 'components/common/fields/TagGroup';
import EmailGroup from 'components/common/fields/EmailGroup';

/**
 * SubQuestToAdd component for displaying individual sub-quests in the attach modal
 * Displays sub-quest information including type, status, and allows selection
 * Both checkbox and Add/Remove button can toggle selection state
 *
 * @param {Object} subquest - The sub-quest data object
 * @param {boolean} checkValue - Whether the sub-quest is currently selected
 * @param {Function} handleCheckboxChange - Handler for checkbox state changes (receives id, name, isChecked)
 */
function SubQuestToAdd({ subQuest, checkValue, handleCheckboxChange }) {
  const TextColor = useColorModeValue('gray.600', 'gray.300');

  const handleButtonClick = () => {
    handleCheckboxChange(subQuest.id, subQuest.name, !checkValue);
  };

  return (
    <Box mb={2} borderWidth="1px" borderRadius="md">
      <Box w="100%" p={2} bg={useColorModeValue('#f7f7f7', 'gray.700')}>
        <Flex alignItems="center" mx={3} gap={3}>
          <Checkbox
            isChecked={checkValue}
            onChange={() =>
              handleCheckboxChange(subQuest.id, subQuest.name, !checkValue)
            }
          />
          <Flex direction="row" justify="space-between" flex="1">
            <Box>
              <Flex direction="row" align="center">
                <SubQuestIcon
                  typeId={subQuest?.type.id}
                  boxSize={10}
                  color="teal.500"
                  mr={4}
                />
                <Box>
                  <Flex align="center" gap={2}>
                    <Text fontSize="md" fontWeight="semibold">
                      <IdTag id={subQuest.id} />
                      &nbsp;
                      {subQuest?.name}
                    </Text>
                    <Tooltip
                      label={subQuest?.isPublished ? 'Published' : 'Draft'}
                    >
                      <Tag
                        size="sm"
                        colorScheme={subQuest?.isPublished ? 'green' : 'purple'}
                        variant="solid"
                      >
                        <TagLeftIcon
                          as={subQuest?.isPublished ? FaCheckCircle : FaPen}
                        />
                        <TagLabel>
                          {subQuest?.isPublished ? 'Published' : 'Draft'}
                        </TagLabel>
                      </Tag>
                    </Tooltip>
                  </Flex>

                  {subQuest?.objective && (
                    <Text fontSize="sm" color={TextColor} noOfLines={2}>
                      {subQuest.objective}
                    </Text>
                  )}

                  <Flex direction="column" align="start" mt={2}>
                    <TagGroup tags={subQuest?.tags} />
                    <EmailGroup
                      mt={2}
                      name="Contributors:"
                      emails={subQuest?.ownerGroups}
                    />
                  </Flex>
                </Box>
              </Flex>
            </Box>
            <Flex justify="flex-end" align="center">
              <Button
                border="1px"
                borderColor="gray.300"
                colorScheme={checkValue ? 'blue' : 'gray'}
                size="sm"
                width="100px"
                leftIcon={checkValue ? <CloseIcon /> : <AddIcon />}
                onClick={handleButtonClick}
              >
                {checkValue ? 'Remove' : 'Add'}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}

SubQuestToAdd.defaultProps = {
  checkValue: false,
};

SubQuestToAdd.propTypes = {
  subQuest: PropTypes.object.isRequired,
  handleCheckboxChange: PropTypes.func.isRequired,
  checkValue: PropTypes.bool,
};

export default SubQuestToAdd;
